<template>
  <div class="section" id="rate-type-selection" v-bind="$attrs">
    <h1 class="title">{{ title }}</h1>
    <p class="description" v-if="!singleRateType">
      Learn more about fixed and variable rates by clicking each box. Once you've selected the rate
      type you prefer, click Continue. Don't worry, you'll still be able to change your selection
      before choosing your repayment term.
    </p>
    <div v-if="(rateTypes || [])?.length <= 1 && !error"></div>
    <div class="buttons" id="rate-types" v-else>
      <button
        v-for="rateType in rateTypes"
        :key="rateType.rateTypeId"
        class="rate-type-button"
        :class="[
          { active: rateType.rateTypeId === selectedRateTypeId && !singleRateType, singleRateType },
        ]"
        @click="selectRateType(rateType)"
      >
        <span class="button-text">{{ formatRateTypeName(rateType.rateTypeName) }}</span>
      </button>
    </div>
    <div
      class="section"
      id="rate-type-description"
      :class="{ single: singleRateType }"
      v-show="selectedRateTypeId > 0"
    >
      <h5>
        {{
          selectedRateType?.rateTypeName ? formatRateTypeName(selectedRateType?.rateTypeName) : ""
        }}
      </h5>
      <p>{{ selectedRateType?.rateTypeDescription || "" }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import useApplicationData from "@/composables/useApplicationData";

const props = withDefaults(defineProps<{ preserveSelection?: boolean }>(), {
  preserveSelection: false,
});

const store = useStore();
const route = useRoute();
const router = useRouter();

const { inlineProductDisplay } = useApplicationData();

const selectedRateTypeId = ref<number>(0);

const rateTypes = computed(
  () => store.state.loanConfigurationModule?.loanOptionsConfiguration?.rateTypeSections,
);

const singleRateType = computed<boolean>(
  () => !store.state.loanConfigurationModule.loanOptionsConfiguration.hasFixedAndVariableRates,
);

const title = computed<string>(() => {
  if (singleRateType.value && rateTypes.value && rateTypes.value?.length > 0) {
    const defaultRateType = rateTypes.value[0];

    return `This ${inlineProductDisplay.value} features a ${(
      defaultRateType?.rateTypeName || ""
    )?.toLowerCase()} interest rate.`;
  }
  return "What type of interest rate works best for you?";
});

const error = computed<boolean>(() => store.state.loanConfigurationModule.error);

function formatRateTypeName(rateType: string) {
  return `${rateType} Rate`;
}

const selectedRateType = computed(() => {
  return (rateTypes.value || [])?.find((r: any) => r.rateTypeId === selectedRateTypeId.value);
});

function selectRateType(rateType: any) {
  selectedRateTypeId.value = rateType.rateTypeId;

  if (props.preserveSelection) {
    router.replace({
      query: {
        ...route.query, // Preserve existing queries
        rate: rateType.rateTypeName, // Add/update the 'page' query
      },
    });
  }
}

function setSelectedRate(rateType: string) {
  const rate = rateTypes.value?.find((r: any) => r.rateTypeName === rateType);
  if (rate) {
    selectedRateTypeId.value = rate?.rateTypeId;
    store.dispatch("loanConfigurationModule/setSelectedRateTypeId", selectedRateTypeId.value);
  }
}

function isActive(rateTypeId: number) {
  return selectedRateTypeId.value === rateTypeId;
}

function resetSelectedRateType() {
  selectedRateTypeId.value = 0;
}

watch(
  () => rateTypes.value?.length,
  (count: number) => {
    if (count === 1) {
      setSelectedRate(rateTypes.value[0].rateTypeName);
    }
  },
  {
    immediate: true,
  },
);

defineExpose({
  isActive,
  rateTypes,
  resetSelectedRateType,
  selectedRateTypeId,
  selectedRateType,
  selectRateType,
  setSelectedRate,
  singleRateType,
  title,
});
</script>
<style scoped src="@/styles/professional-grad/substep.css"></style>
<style scoped src="@/styles/professional-grad/button.css"></style>
<style scoped src="@/styles/professional-grad/substep-2.css"></style>
